/**
 * Viewport Height Tracker
 * Dynamically measures and tracks header and announcement bar heights
 * Updates CSS variables for accurate viewport calculations
 */

class ViewportHeightTracker {
  constructor() {
    this.announcementBar = null
    this.header = null
    this.resizeObserver = null

    this.init()
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupTracking())
    } else {
      this.setupTracking()
    }
  }

  setupTracking() {
    // Find the announcement bar and header elements
    this.findElements()

    // Initial measurement
    this.updateHeights()

    // Set up ResizeObserver for dynamic updates
    this.setupResizeObserver()

    // Also listen for window resize as backup
    window.addEventListener(
      'resize',
      this.debounce(() => this.updateHeights(), 100)
    )
  }

  findElements() {
    // Common selectors for announcement bar
    const announcementSelectors = [
      '.announcement-bar',
      '[data-section-type="announcement-bar"]',
      '#announcement-bar',
      '.shopify-section--announcement-bar',
      '.section-announcement-bar',
    ]

    // Common selectors for header
    const headerSelectors = [
      'header',
      '.header',
      '.site-header',
      '#header',
      '.shopify-section--header',
      '.section-header',
    ]

    // Find announcement bar
    for (const selector of announcementSelectors) {
      const element = document.querySelector(selector)
      if (element && this.isVisible(element)) {
        this.announcementBar = element
        break
      }
    }

    // Find header
    for (const selector of headerSelectors) {
      const element = document.querySelector(selector)
      if (element && this.isVisible(element)) {
        this.header = element
        break
      }
    }

    // Debug logging
    console.log('Viewport Height Tracker:', {
      announcementBar: this.announcementBar,
      header: this.header,
    })
  }

  isVisible(element) {
    const style = window.getComputedStyle(element)
    return style.display !== 'none' && style.visibility !== 'hidden' && element.offsetHeight > 0
  }

  updateHeights() {
    let announcementHeight = 0
    let headerHeight = 0

    // Measure announcement bar height
    if (this.announcementBar && this.isVisible(this.announcementBar)) {
      announcementHeight = this.announcementBar.offsetHeight
    }

    // Measure header height
    if (this.header && this.isVisible(this.header)) {
      headerHeight = this.header.offsetHeight
    }

    // Update CSS variables
    document.documentElement.style.setProperty('--announcement-bar-height', `${announcementHeight}px`)
    document.documentElement.style.setProperty('--header-height', `${headerHeight}px`)

    // Recalculate available height
    const availableHeight = `calc(100vh - ${announcementHeight}px - ${headerHeight}px)`
    document.documentElement.style.setProperty('--available-height', availableHeight)

    // Debug logging
    console.log('Heights updated:', {
      announcement: announcementHeight,
      header: headerHeight,
      available: availableHeight,
    })
  }

  setupResizeObserver() {
    if (!window.ResizeObserver) {
      console.warn('ResizeObserver not supported, falling back to window resize events')
      return
    }

    this.resizeObserver = new ResizeObserver((entries) => {
      // Debounce the update to avoid excessive calls
      this.debouncedUpdate()
    })

    // Observe both elements if they exist
    if (this.announcementBar) {
      this.resizeObserver.observe(this.announcementBar)
    }

    if (this.header) {
      this.resizeObserver.observe(this.header)
    }
  }

  // Debounced update method
  debouncedUpdate = this.debounce(() => this.updateHeights(), 300)

  // Utility function for debouncing
  debounce(func, wait) {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }

  // Method to manually trigger height recalculation
  recalculate() {
    // Disconnect existing ResizeObserver to avoid memory leaks
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }

    // Find elements and update heights
    this.findElements()
    this.updateHeights()

    // Re-initialize ResizeObserver with new elements
    this.setupResizeObserver()
  }

  // Cleanup method
  destroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
  }
}

// Initialize tracker
const viewportTracker = new ViewportHeightTracker()

// Expose globally for manual recalculation if needed
window.viewportTracker = viewportTracker

// Handle page transitions (for SPAs or dynamic content)
document.addEventListener('shopify:section:load', () => {
  setTimeout(() => viewportTracker.recalculate(), 100)
})

document.addEventListener('shopify:section:unload', () => {
  setTimeout(() => viewportTracker.recalculate(), 100)
})
